import axios from "axios";
import { isAustralianApiUrl } from "@journeys/utils/helpers";
import config from "@/config/constants";
import { devError } from "@/utils/devLog/devLog";

export type TimezoneOption = {
  id: string;
  label: string;
  offset: string;
};

export type GetTimezonesResponse = {
  success: boolean;
  timezones?: TimezoneOption[];
  err?: string;
};

export type GetTimezoneResponse = {
  success: boolean;
  // The effective zone — always present and always valid, so safe to display as-is.
  timezone?: string;
  // The raw stored value; null means the member has never chosen one.
  user_timezone?: string | null;
  is_default?: boolean;
  err?: string;
};

export type UpdateTimezoneResponse = {
  success: boolean;
  err?: string;
};

const AU_DEFAULT_TIMEZONE = "Australia/Sydney";
const EU_DEFAULT_TIMEZONE = "Europe/Dublin";

// Mirrors react-native-prototype src/config/timezone.ts, which reads EXPO_PUBLIC_REGION —
// keep the two in step so a member gets the same default on web and mobile. Never derived
// from the device clock: auto-detection was rejected because it misfires for travelling users.
export const regionalDefaultTimezone = (
  region: string = process.env.VUE_APP_REGION,
): string => (region === "AU" ? AU_DEFAULT_TIMEZONE : EU_DEFAULT_TIMEZONE);

export const isTimezoneSupportEnabled = (
  apiUrl: string = process.env.VUE_APP_API_PATH,
): boolean => isAustralianApiUrl(apiUrl);

export const responseTimezoneHeaders = (): Record<string, string> =>
  isTimezoneSupportEnabled() ? { "X-Response-Timezone": "UTC" } : {};

export const findTimezoneOption = (
  options: TimezoneOption[],
  timezoneId: string | null | undefined,
): TimezoneOption | null =>
  timezoneId
    ? options.find((option) => option.id === timezoneId) ?? null
    : null;

const timezoneUrl = (action: string): string =>
  `${config.api_env}/application/api/u/user/${action}`;

/**
 * Public — no token, so it can populate the sign-up dropdown before the user exists.
 * `label`/`offset` reflect DST at request time, so the list must not be cached across a
 * session: every Australia/* label shifts by an hour at each DST transition.
 */
export const fetchTimezones = async (): Promise<TimezoneOption[]> => {
  try {
    const response = await axios.post(
      timezoneUrl("get-timezones"),
      {},
      config.options,
    );
    const data: GetTimezonesResponse = response?.data;
    return data?.success && Array.isArray(data.timezones) ? data.timezones : [];
  } catch (error) {
    devError("[timezone] fetchTimezones failed", error);
    return [];
  }
};

/** userId is the API auth token (store getter `user_id`), as with other api/u/user calls. */
export const fetchUserTimezone = async (
  userId: string | number,
): Promise<GetTimezoneResponse | null> => {
  try {
    const response = await axios.post(
      timezoneUrl("get-timezone"),
      { user_id: userId },
      config.options,
    );
    return response?.data ?? null;
  } catch (error) {
    devError("[timezone] fetchUserTimezone failed", error);
    return null;
  }
};

/**
 * Strict, unlike sign-up: an unrecognised identifier comes back success:false with `err`
 * and leaves the stored value unchanged. Surface that message rather than swallowing it.
 */
export const updateUserTimezone = async (
  userId: string | number,
  timezone: string,
): Promise<UpdateTimezoneResponse | null> => {
  try {
    const response = await axios.post(
      timezoneUrl("update-timezone"),
      { user_id: userId, timezone },
      config.options,
    );
    return response?.data ?? null;
  } catch (error) {
    devError("[timezone] updateUserTimezone failed", error);
    return null;
  }
};
